import React, { useState, useRef, useEffect, useContext } from 'react'

import { useNavigate, Link } from 'react-router-dom'

import logoBlack from '../../../../images/logo.png'
import logoWhite from '../../../../images/logo-w.png'

import MenuTextItem from './components/MenuTextItem'
import MenuIconItem from './components/MenuIconItem'
import SubMenuItem from './components/SubMenuItem'

import { TextLink } from './types'
import './NavBar.css'
import { useAuth } from '../../../../hooks/auth'
import { getUserData } from '../../../../services/auth'

import { ThemeContext } from '../../../../components/ThemeContext'
import { OrganizationInfo } from '../../Org/Home/types'
import ActionMenu, {
    ActionMenuItem,
} from '../../Contracts/Edit/Contracts/components/ActionMenu'

type User = {
    token: string
    object: any
    u_role: string | null
}

const MENU_KEY_ORG = 'org'
const MENU_KEY_USER = 'user'
const MENU_KEY_REFERENCE = 'ref'
const MENU_KEY_STUDY = 'study'

const SUB_MENU_ITEMS = new Map<string, TextLink[]>([
    [
        MENU_KEY_ORG,
        [
            { link: 'org/home', text: 'Организация', access: 'all' },
            { link: 'org/users', text: 'Пользователи', access: 'no_all' },
            { link: 'org/structure', text: 'Структура', access: 'no_all' },
            { link: 'org/roles', text: 'Роли', access: 'no_all' },
            { link: 'org/methods', text: 'Способы закупки', access: 'no_all' },
            { link: 'org/lists', text: 'Перечни', access: 'no_all' },
        ],
    ],
    [
        MENU_KEY_USER,
        [
            { link: 'home', text: 'Главная страница', access: 'all' },
            { link: 'plans', text: 'План закупки', access: 'all' },
            { link: 'purchases', text: 'Закупки', access: 'no_all' },
            { link: 'contracts', text: 'Договоры', access: 'no_all' },
            { link: 'performances', text: 'Исполнения', access: 'no_all' },
            { link: 'approvals/plans', text: 'Согласование', access: 'all' },
        ],
    ],
    [
        MENU_KEY_REFERENCE,
        [
            { link: 'org/handbook/okpd2', text: 'ОКПД2', access: 'all' },
            { link: 'org/handbook/okved2', text: 'ОКВЭД2', access: 'all' },
        ],
    ],
    [
        MENU_KEY_STUDY,
        [{ link: 'org/study/template', text: 'Шаблоны', access: 'all' }],
    ],
])

const NavBar: React.FC = () => {
    const initialState = window.location.hash.startsWith('#/hub/org')
        ? MENU_KEY_ORG
        : MENU_KEY_USER
    const [currentMenuItem, setCurrentMenuItem] = useState(initialState)

    const { user, logout } = useAuth()
    const [roleName, setRoleName] = useState<string | null>(null)

    const { theme, toggleTheme } = useContext(ThemeContext)
    const logo = theme === 'light' ? logoBlack : logoWhite

    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const navigate = useNavigate()

    const [orgData, setOrgData] = useState<OrganizationInfo>()
    const [orgSwitchData, setOrgSwitchData] = useState<OrganizationInfo[]>([])
    const [isLoading, setLoading] = useState<boolean>(false)
    const [isLoadingSwitchOrg, setLoadingSwitchOrg] = useState<boolean>(false)

    const switchOrgBtnRef = useRef<HTMLButtonElement | null>(null)
    const [switchMenuOpen, setSwitchMenuOpen] = useState(false)

    const [isNotOrg, setNotOrg] = useState('')

    const handleLogout = () => {
        let apiUrl: string | undefined
        if (process.env.REACT_APP_MODE == 'system') {
            apiUrl = 'https://noxsys.ru'
        } else {
            apiUrl = process.env.REACT_APP_API_URL
        }

        fetch(apiUrl + '/api/auth/v1/users/logout/', {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }).then((r) => {
            if (r.ok) {
                logout()
            }
        })
    }

    const loadOrganizationData = async (): Promise<OrganizationInfo> => {
        setLoading(true)
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/organization',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then(async (response) => {
                if (response.ok) {
                    const data = await response.json()
                    setOrgData(data)
                    return data
                } else {
                    throw new Error('Failed to fetch organization')
                }
            })
            .catch((e) => {
                console.error(e)
            })
            .finally(() => setLoading(false))
    }

    const loadOrganizationSwitchData = async (): Promise<
        OrganizationInfo[]
    > => {
        setLoadingSwitchOrg(true)
        return fetch(
            process.env.REACT_APP_API_URL +
                '/api/backend/v1/user/organizations',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then(async (response) => {
                if (response.ok) {
                    const data = await response.json()
                    setOrgSwitchData(data)
                    return data
                } else {
                    throw new Error('Failed to fetch switch organization')
                }
            })
            .catch((e) => {
                console.error(e)
            })
            .finally(() => setLoadingSwitchOrg(false))
    }

    const handleSwitchOrganization = async (org_id: string) => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/auth/v1/users/switch-organization/`

        try {
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ org_id }),
            })

            if (!response.ok) {
                throw new Error(`Ошибка ${response.status}`)
            }

            console.log('Переключил на другую организацию')
            window.location.reload()
        } catch (error) {
            console.error('Не смог переключить на другую организацию', error)
        }
    }

    const switchMenuItems: ActionMenuItem[] =
        orgSwitchData.length > 1
            ? orgSwitchData.map((org) => ({
                  key: org.guid,
                  label: org.menu_name,
                  onClick: () => handleSwitchOrganization(org.guid),
                  logo: org.logo_url,
              }))
            : []

    useEffect(() => {
        const handleUserData = async () => {
            const userData = await getUserData()
            if (userData) {
                setRoleName(userData.u_role) // Сохраняем u_role в state
                setNotOrg(userData.object.org_id)
            }

            const organization = await loadOrganizationData()
            if (organization) {
                setOrgData(organization)
            }

            const organizations = await loadOrganizationSwitchData()
            if (organizations) {
                setOrgSwitchData(organizations)
            }
        }
        handleUserData()
    }, [])

    const toggleDropdown = () => {
        setIsOpen(!isOpen)
    }

    const handleClickOutside = (event: MouseEvent) => {
        if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target as Node)
        ) {
            setIsOpen(false)
        }
    }

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        } else {
            document.removeEventListener('mousedown', handleClickOutside)
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    return (
        <>
            <div className="h-full flex flex-col p-1 pb-2.5 items-center bg-[#e5e5e5] dark:bg-[#181818]">
                <a href="#/">
                    <img src={logo} alt="Логотип" className="w-10 h-10 mb-3" />
                </a>

                {switchMenuItems.length > 1 ? (
                    <>
                        <button
                            ref={switchOrgBtnRef}
                            className="flex w-12 h-12 mb-2 rounded-lg items-center p-1 cursor-pointer hover:bg-[#cccccc] dark:hover:bg-[#464447]"
                            type="button"
                            onClick={() => setSwitchMenuOpen((prev) => !prev)}
                        >
                            <div className="flex w-10 h-10 rounded-full items-center justify-center text-center text-[12.8px] text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#f6f6f6] dark:bg-[#262427]">
                                {orgData?.logo_url ? (
                                    <img
                                        src={`${process.env.REACT_APP_API_URL}${orgData.logo_url}`}
                                        alt="Логотип организации"
                                        className="flex w-10 h-10 object-cover"
                                    />
                                ) : isLoading ? (
                                    <svg
                                        className={`animate-spin h-4 w-4 ${
                                            theme === 'dark'
                                                ? 'text-[#FFFFFF]'
                                                : 'text-[#181818]'
                                        }`}
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                ) : (
                                    'logo'
                                )}
                            </div>
                        </button>

                        <ActionMenu
                            open={switchMenuOpen}
                            anchorRef={switchOrgBtnRef}
                            onClose={() => setSwitchMenuOpen(false)}
                            items={switchMenuItems}
                            placement="right-start"
                            theme={theme}
                        />
                    </>
                ) : (
                    <div className="flex w-12 h-12 mb-2 rounded-lg items-center p-1">
                        <div className="flex w-10 h-10 rounded-full items-center justify-center text-center text-[12.8px] text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#f6f6f6] dark:bg-[#262427]">
                            {orgData?.logo_url ? (
                                <img
                                    src={`${process.env.REACT_APP_API_URL}${orgData.logo_url}`}
                                    alt="Логотип организации"
                                    className="flex w-10 h-10 object-cover"
                                />
                            ) : isLoading ? (
                                <svg
                                    className={`animate-spin h-4 w-4 ${
                                        theme === 'dark'
                                            ? 'text-[#FFFFFF]'
                                            : 'text-[#181818]'
                                    }`}
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                    ></path>
                                </svg>
                            ) : (
                                'logo'
                            )}
                        </div>
                    </div>
                )}

                <ul className="flex flex-col gap-y-1 grow items-center">
                    <MenuIconItem
                        isActive={currentMenuItem === MENU_KEY_ORG}
                        theme={theme}
                        onClick={() => {
                            setCurrentMenuItem(MENU_KEY_ORG)
                        }}
                        icon={`M20,8h0L14,2.74a3,3,0,0,0-4,0L4,8a3,3,0,0,0-1,2.26V19a3,3,0,0,0,3,3H18a3,3,0,0,0,3-3V10.25A3,3,0,0,0,20,8ZM14,20H10V15a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1Zm5-1a1,1,0,0,1-1,1H16V15a3,3,0,0,0-3-3H11a3,3,0,0,0-3,3v5H6a1,1,0,0,1-1-1V10.25a1,1,0,0,1,.34-.75l6-5.25a1,1,0,0,1,1.32,0l6,5.25a1,1,0,0,1,.34.75Z`}
                    />
                    <li className="divider" />
                    <MenuTextItem
                        text="223"
                        isActive={currentMenuItem === MENU_KEY_USER}
                        theme={theme}
                        onClick={() => {
                            setCurrentMenuItem(MENU_KEY_USER)
                        }}
                    />
                    <li className="divider" />
                    <MenuIconItem
                        isActive={currentMenuItem === MENU_KEY_REFERENCE}
                        theme={theme}
                        onClick={() => {
                            setCurrentMenuItem(MENU_KEY_REFERENCE)
                        }}
                        icon={`M22.47,18.82l-1-3.86h0L18.32,3.37a1,1,0,0,0-1.22-.71l-3.87,1a1,1,0,0,0-.73-.33H2.5a1,1,0,0,0-1,1v16a1,1,0,0,0,1,1h10a1,1,0,0,0,1-1v-8l2.2,8.22a1,1,0,0,0,1,.74,1.15,1.15,0,0,0,.26,0l4.83-1.29a1,1,0,0,0,.61-.47A1.05,1.05,0,0,0,22.47,18.82Zm-16,.55h-3v-2h3Zm0-4h-3v-6h3Zm0-8h-3v-2h3Zm5,12h-3v-2h3Zm0-4h-3v-6h3Zm0-8h-3v-2h3Zm2.25-1.74,2.9-.78.52,1.93-2.9.78Zm2.59,9.66-1.55-5.8,2.9-.78,1.55,5.8Zm1,3.86-.52-1.93,2.9-.78.52,1.93Z`}
                    />
                    <li className="divider" />
                    <MenuIconItem
                        isActive={currentMenuItem === MENU_KEY_STUDY}
                        theme={theme}
                        onClick={() => {
                            setCurrentMenuItem(MENU_KEY_STUDY)
                        }}
                        icon={`M21.17,2.06A13.1,13.1,0,0,0,19,1.87a12.94,12.94,0,0,0-7,2.05,12.94,12.94,0,0,0-7-2,13.1,13.1,0,0,0-2.17.19,1,1,0,0,0-.83,1v12a1,1,0,0,0,1.17,1,10.9,10.9,0,0,1,8.25,1.91l.12.07.11,0a.91.91,0,0,0,.7,0l.11,0,.12-.07A10.9,10.9,0,0,1,20.83,16a1,1,0,0,0,1.17-1v-12A1,1,0,0,0,21.17,2.06ZM11,15.35a12.87,12.87,0,0,0-6-1.48c-.33,0-.66,0-1,0v-10a8.69,8.69,0,0,1,1,0,10.86,10.86,0,0,1,6,1.8Zm9-1.44c-.34,0-.67,0-1,0a12.87,12.87,0,0,0-6,1.48V5.67a10.86,10.86,0,0,1,6-1.8,8.69,8.69,0,0,1,1,0Zm1.17,4.15A13.1,13.1,0,0,0,19,17.87a12.94,12.94,0,0,0-7,2.05,12.94,12.94,0,0,0-7-2.05,13.1,13.1,0,0,0-2.17.19A1,1,0,0,0,2,19.21,1,1,0,0,0,3.17,20a10.9,10.9,0,0,1,8.25,1.91,1,1,0,0,0,1.16,0A10.9,10.9,0,0,1,20.83,20,1,1,0,0,0,22,19.21,1,1,0,0,0,21.17,18.06Z`}
                    />
                </ul>
            </div>
            <div className="h-full flex flex-col min-w-[155px] p-2 bg-[#f6f6f6] dark:bg-[#262427]">
                <div className="mb-5 text-[#181818] dark:text-[#FFFFFF] uppercase leading-none">
                    <span className="text-[#181818] dark:text-[#FFFFFF] uppercase leading-none font-semibold items-center justify-start text-left">
                        ТЕХНОЛОГИИ
                        <br />
                        ЗАКУПОК
                    </span>
                </div>
                {isLoading ? (
                    <div className="flex w-[145px] h-10 mb-3 p-2.5 items-center justify-start text-left text-[12.8px] text-[#181818] dark:text-[#fff] bg-[#f6f6f6] dark:bg-[#262427]">
                        <svg
                            className={`animate-spin h-4 w-4 ${
                                theme === 'dark'
                                    ? 'text-[#FFFFFF]'
                                    : 'text-[#181818]'
                            }`}
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            ></circle>
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            ></path>
                        </svg>
                    </div>
                ) : (
                    <div className="flex w-[145px] h-10 mb-3 p-2.5 items-center justify-start text-left text-[12.8px] text-[#181818] dark:text-[#fff] bg-[#f6f6f6] dark:bg-[#262427]">
                        <p className="towrow font-bold">{orgData?.menu_name}</p>
                    </div>
                )}

                <ul className="flex flex-col grap-y-1 grow">
                    {(SUB_MENU_ITEMS.get(currentMenuItem) as TextLink[]).map(
                        (item, index) =>
                            item.access == 'all' ? (
                                <SubMenuItem
                                    key={index}
                                    link={item.link}
                                    text={item.text}
                                    access={item.access}
                                />
                            ) : isNotOrg ==
                              'cf2a2735-2527-4b18-b4f0-2fd11f588e3c' ? (
                                item.access == 'no_all_stng' ? (
                                    ''
                                ) : (
                                    <SubMenuItem
                                        key={index}
                                        link={item.link}
                                        text={item.text}
                                        access={item.access}
                                    />
                                )
                            ) : item.access != 'test' &&
                              (roleName == 'admin' ||
                                  roleName == 'specialist') ? (
                                <SubMenuItem
                                    key={index}
                                    link={item.link}
                                    text={item.text}
                                    access={item.access}
                                />
                            ) : user?.object.login == 'user1@example.com' ||
                              user?.object.login == 'user2@example.com' ||
                              user?.object.login == 'user3@example.com' ||
                              user?.object.login == 'user4@example.com' ||
                              user?.object.login == 'userstng@example.com' ||
                              user?.object.login == 'userpolar@example.com' ||
                              user?.object.login == 'userlorp@example.com' ||
                              user?.object.login == 'usersngs@example.com' ||
                              user?.object.login == 'useryxk@example.com' ||
                              user?.object.login == 'userport@example.com' ||
                              user?.object.login == 'userkap@example.com' ? (
                                <SubMenuItem
                                    key={index}
                                    link={item.link}
                                    text={item.text}
                                    access={item.access}
                                />
                            ) : (
                                ''
                            )
                    )}
                </ul>
                <div
                    className="relative inline-block text-left"
                    ref={dropdownRef}
                >
                    <div
                        className="flex flex-row h-10 items-center justify-start p-2 rounded text-xs text-[#181818] hover:bg-[#e9e9e9] hover:text-[#000000] dark:text-[#8A8A8A] dark:hover:bg-[#464447] dark:hover:text-[#FFFFFF] cursor-pointer gap-1"
                        onClick={toggleDropdown}
                    >
                        <div className="grow items-center justify-center text-xs">
                            {user?.object.login.split('@')[0]}
                        </div>{' '}
                        <svg
                            className="w-5 h-5"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            id="setting"
                        >
                            <path
                                fill={theme == 'dark' ? '#FFFFFF' : '#181818'}
                                d="M19.9 12.66a1 1 0 0 1 0-1.32l1.28-1.44a1 1 0 0 0 .12-1.17l-2-3.46a1 1 0 0 0-1.07-.48l-1.88.38a1 1 0 0 1-1.15-.66l-.61-1.83a1 1 0 0 0-.95-.68h-4a1 1 0 0 0-1 .68l-.56 1.83a1 1 0 0 1-1.15.66L5 4.79a1 1 0 0 0-1 .48L2 8.73a1 1 0 0 0 .1 1.17l1.27 1.44a1 1 0 0 1 0 1.32L2.1 14.1a1 1 0 0 0-.1 1.17l2 3.46a1 1 0 0 0 1.07.48l1.88-.38a1 1 0 0 1 1.15.66l.61 1.83a1 1 0 0 0 1 .68h4a1 1 0 0 0 .95-.68l.61-1.83a1 1 0 0 1 1.15-.66l1.88.38a1 1 0 0 0 1.07-.48l2-3.46a1 1 0 0 0-.12-1.17ZM18.41 14l.8.9-1.28 2.22-1.18-.24a3 3 0 0 0-3.45 2L12.92 20h-2.56L10 18.86a3 3 0 0 0-3.45-2l-1.18.24-1.3-2.21.8-.9a3 3 0 0 0 0-4l-.8-.9 1.28-2.2 1.18.24a3 3 0 0 0 3.45-2L10.36 4h2.56l.38 1.14a3 3 0 0 0 3.45 2l1.18-.24 1.28 2.22-.8.9a3 3 0 0 0 0 3.98Zm-6.77-6a4 4 0 1 0 4 4 4 4 0 0 0-4-4Zm0 6a2 2 0 1 1 2-2 2 2 0 0 1-2 2Z"
                            ></path>
                        </svg>
                    </div>
                    {isOpen && (
                        <div
                            className="absolute flex flex-col w-[170px] shadow-lg rounded-lg p-1 z-50 text-[12.8px] items-center justify-start gap-1 bg-[#d9dce0] dark:bg-[#333033] border border-1 border-[#b8b8b8] dark:border-[#696769]"
                            style={{ bottom: '100%' }}
                        >
                            <div className="flex w-full items-center rounded text-[#181818] hover:text-[#000000] hover:bg-[#e9e9e9] dark:text-[#FFFFFF] dark:hover:text-[#FFFFFF] dark:hover:bg-[#656365]">
                                <button
                                    className="flex flex-1 flex-row py-2 pl-1 w-full text-xs text-left  h-full gap-2"
                                    onClick={() => {
                                        navigate(
                                            `/hub/org/users/${user?.object.user_id}`
                                        )
                                        setIsOpen(!isOpen)
                                    }}
                                >
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        data-name="Layer 1"
                                        viewBox="0 0 24 24"
                                        id="user-circle"
                                    >
                                        <path
                                            fill={
                                                theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                            }
                                            d="M12,2A10,10,0,0,0,4.65,18.76h0a10,10,0,0,0,14.7,0h0A10,10,0,0,0,12,2Zm0,18a8,8,0,0,1-5.55-2.25,6,6,0,0,1,11.1,0A8,8,0,0,1,12,20ZM10,10a2,2,0,1,1,2,2A2,2,0,0,1,10,10Zm8.91,6A8,8,0,0,0,15,12.62a4,4,0,1,0-6,0A8,8,0,0,0,5.09,16,7.92,7.92,0,0,1,4,12a8,8,0,0,1,16,0A7.92,7.92,0,0,1,18.91,16Z"
                                        ></path>
                                    </svg>
                                    Учетная запись
                                </button>
                            </div>
                            <div className="flex w-full items-center rounded text-[#181818] hover:text-[#000000] hover:bg-[#e9e9e9] dark:text-[#FFFFFF] dark:hover:text-[#FFFFFF] dark:hover:bg-[#656365]">
                                <button
                                    className="flex dark:hidden flex-1 flex-row py-2 pl-1 w-full text-xs text-left h-full gap-2"
                                    onClick={toggleTheme}
                                >
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill={
                                            theme == 'dark'
                                                ? '#FFFFFF'
                                                : '#181818'
                                        }
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z"
                                        />
                                    </svg>
                                    Темная тема
                                </button>
                                <button
                                    className="hidden dark:flex flex-1 flex-row py-2 pl-1 w-full text-xs text-left h-full gap-2"
                                    onClick={() => toggleTheme()}
                                >
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill={
                                            theme == 'dark'
                                                ? '#FFFFFF'
                                                : '#181818'
                                        }
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
                                        />
                                    </svg>
                                    Светлая тема
                                </button>
                            </div>
                            <div className="flex w-full items-center rounded text-[#181818] hover:text-[#000000] hover:bg-[#e9e9e9] dark:text-[#FFFFFF] dark:hover:text-[#FFFFFF] dark:hover:bg-[#656365]">
                                <button
                                    className="flex flex-1 flex-row py-2 pl-1 w-full text-xs text-left h-full gap-2"
                                    onClick={handleLogout}
                                >
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        data-name="Layer 1"
                                        viewBox="0 0 24 24"
                                        id="user-circle"
                                    >
                                        <path
                                            fill={
                                                theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                            }
                                            d="M4,12a1,1,0,0,0,1,1h7.59l-2.3,2.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0l4-4a1,1,0,0,0,.21-.33,1,1,0,0,0,0-.76,1,1,0,0,0-.21-.33l-4-4a1,1,0,1,0-1.42,1.42L12.59,11H5A1,1,0,0,0,4,12ZM17,2H7A3,3,0,0,0,4,5V8A1,1,0,0,0,6,8V5A1,1,0,0,1,7,4H17a1,1,0,0,1,1,1V19a1,1,0,0,1-1,1H7a1,1,0,0,1-1-1V16a1,1,0,0,0-2,0v3a3,3,0,0,0,3,3H17a3,3,0,0,0,3-3V5A3,3,0,0,0,17,2Z"
                                        ></path>
                                    </svg>
                                    Выход
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                {/* <Link
                    to={`/hub/org/users/${user?.object.user_id}`}
                    title={user?.object.login.split('@')[0]}
                    className="flex flex-row h-10 items-center p-2.5 rounded text-xs text-[#8A8A8A] hover:bg-[#464447] hover:text-[#FFFFFF]"
                >
                    <div className="grow">
                        {user?.object.login.split('@')[0]}
                    </div>{' '}
                    <Cog6ToothIcon className="h-5 w-5" />
                </Link> */}
            </div>
        </>
    )
}

export default NavBar

// Tailwind CSS custom animation class
// Add the following to your Tailwind CSS configuration or <style> tag in the index.css file:
/* 
@keyframes fade-slide-down {
  0% {
    transform: translateY(-50%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
.animate-fade-slide-down {
  animation: fade-slide-down 0.3s ease-out;
}
*/
